import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import analyticsService from '../services/analyticsService';
import safeStorage from '../utils/safeStorage';
import { ensureCartLineShippingFields } from '../utils/cartShippingEligibility';

const CartContext = createContext();

const isDev = process.env.NODE_ENV !== 'production';

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [cartLoaded, setCartLoaded] = useState(false);

  useEffect(() => {
    if (isDev) {
      console.log('[CartContext] hydrating from localStorage');
    }
    const savedCart = safeStorage.getItem('rainbowSails_cart');
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        const normalized = Array.isArray(parsedCart)
          ? parsedCart.map(ensureCartLineShippingFields)
          : [];
        setCartItems(normalized);
        if (JSON.stringify(normalized) !== JSON.stringify(parsedCart)) {
          safeStorage.setItem('rainbowSails_cart', JSON.stringify(normalized));
        }
      } catch (error) {
        console.error('Failed to parse saved cart:', error);
        safeStorage.removeItem('rainbowSails_cart');
      }
    }
    setCartLoaded(true);
  }, []);

  const addToCart = useCallback((product, quantity = 1) => {
    const line = ensureCartLineShippingFields(product);
    analyticsService.trackAddToCart(line, quantity);

    setCartItems((prevItems) => {
      const existingItem = prevItems.find((item) => item.id === line.id);

      if (existingItem) {
        const updatedItems = prevItems.map((item) =>
          item.id === line.id
            ? { ...ensureCartLineShippingFields(item), quantity: item.quantity + quantity }
            : item
        );
        safeStorage.setItem('rainbowSails_cart', JSON.stringify(updatedItems));
        return updatedItems;
      }
      const newItems = [...prevItems, { ...line, quantity }];
      safeStorage.setItem('rainbowSails_cart', JSON.stringify(newItems));
      return newItems;
    });
  }, []);

  const removeFromCart = useCallback((productId) => {
    setCartItems((prevItems) => {
      const removedItem = prevItems.find((item) => item.id === productId);
      if (removedItem) {
        analyticsService.trackRemoveFromCart(removedItem, removedItem.quantity);
      }

      const newItems = prevItems.filter((item) => item.id !== productId);
      safeStorage.setItem('rainbowSails_cart', JSON.stringify(newItems));
      return newItems;
    });
  }, []);

  const updateQuantity = useCallback(
    (productId, quantity) => {
      if (quantity <= 0) {
        removeFromCart(productId);
        return;
      }

      setCartItems((prevItems) => {
        const updatedItems = prevItems.map((item) =>
          item.id === productId
            ? { ...ensureCartLineShippingFields(item), quantity }
            : item
        );
        safeStorage.setItem('rainbowSails_cart', JSON.stringify(updatedItems));
        return updatedItems;
      });
    },
    [removeFromCart]
  );

  const clearCart = useCallback(() => {
    setCartItems([]);
    safeStorage.removeItem('rainbowSails_cart');
  }, []);

  // Stable identity but reads current cartItems via closure — recreated only when cart changes
  const getTotalItems = useCallback(
    () => cartItems.reduce((total, item) => total + item.quantity, 0),
    [cartItems]
  );

  const getTotalPrice = useCallback(
    () =>
      cartItems.reduce(
        (total, item) => total + item.price * item.quantity,
        0
      ),
    [cartItems]
  );

  const value = useMemo(
    () => ({
      cartItems,
      cartLoaded,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      getTotalItems,
      getTotalPrice,
    }),
    [
      cartItems,
      cartLoaded,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      getTotalItems,
      getTotalPrice,
    ]
  );

  return (
    <CartContext.Provider value={value}>{children}</CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    if (isDev) {
      console.warn('useCart used outside CartProvider - returning default values');
    }
    return {
      cartItems: [],
      cartLoaded: false,
      addToCart: () => {},
      removeFromCart: () => {},
      updateQuantity: () => {},
      clearCart: () => {},
      getTotalItems: () => 0,
      getTotalPrice: () => 0,
    };
  }
  return context;
};

export default CartContext;
